import { createContext, useCallback, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import api from './api';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthState,
} from '../types/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as User;
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      const response = await api.post<{ token: string; refresh_token?: string }>(
        '/api/login',
        credentials
      );
      const { token, refresh_token } = response.data;

      localStorage.setItem('token', token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      // Decode JWT to get user info (basic decode, not validation)
      let user: User;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        user = {
          id: payload.sub || '',
          email: payload.username || credentials.email,
          firstName: payload.firstName || '',
          lastName: payload.lastName || '',
          roles: payload.roles || ['ROLE_USER'],
          createdAt: new Date().toISOString(),
          isActive: true,
          // Freischaltung wird erst beim naechsten Login aktualisiert, falls die VDZ sie
          // waehrend einer laufenden Sitzung aendert (bestehendes Verhalten wie bei Rollen).
          enabledReportCategories: payload.enabledReportCategories || [],
        };
      } catch {
        // Fallback if JWT payload is malformed
        user = {
          id: '',
          email: credentials.email,
          firstName: '',
          lastName: '',
          roles: ['ROLE_USER'],
          createdAt: new Date().toISOString(),
          isActive: true,
          enabledReportCategories: [],
        };
      }

      localStorage.setItem('user', JSON.stringify(user));

      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });

      notifications.show({
        title: 'Erfolgreich angemeldet',
        message: 'Willkommen zurück!',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Anmeldung fehlgeschlagen',
        message: 'Bitte überprüfen Sie Ihre Zugangsdaten.',
        color: 'red',
      });
      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    try {
      await api.post('/api/register', {
        email: data.email,
        plainPassword: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      notifications.show({
        title: 'Registrierung erfolgreich',
        message: 'Sie können sich jetzt anmelden.',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Registrierung fehlgeschlagen',
        message: 'Bitte versuchen Sie es erneut.',
        color: 'red',
      });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    // Invalidate refresh token on server
    if (refreshToken) {
      try {
        await api.post('/api/logout', { refresh_token: refreshToken });
      } catch {
        // Continue with local logout even if server call fails
      }
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    notifications.show({
      title: 'Abgemeldet',
      message: 'Auf Wiedersehen!',
      color: 'blue',
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
